import React from 'react';
import { cn, Messages } from '@tasama/ui';

export interface FlowResultScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Controls which message variant is rendered.
   * Defaults to 'success'.
   */
  type?: 'success' | 'error';
  /**
   * Heading text. Required — callers must resolve domain-specific defaults
   * before passing here. This component does not know about per-feature copy.
   */
  title: string;
  /**
   * Body text. Required for the same reason as title.
   */
  description: string;
  /** Test ID for QA automation. Propagated to the inner Messages component. */
  testID?: string;
}

/**
 * FlowResultScreen
 *
 * Shared presentational base for every workflow result screen in the portal.
 * Renders a centred Messages block inside the standard result container.
 *
 * It knows nothing about per-feature defaults or navigation — those concerns
 * belong to the domain wrapper (NDAResultScreen, SCResultScreen, …) and the
 * parent layout respectively.
 */
const FlowResultScreen: React.FC<FlowResultScreenProps> = ({
  type = 'success',
  title,
  description,
  className,
  testID,
  ...props
}) => {
  const messageType = type === 'error' ? 'error' : 'default';

  return (
    <div
      data-testid={testID}
      className={cn(
        'flex flex-col justify-center items-center gap-8 w-full flex-1',
        className
      )}
      {...props}
    >
      <Messages
        type={messageType}
        title={title}
        description={description}
        testID={`${testID ?? 'flow-result'}-${type}-message`}
      />
    </div>
  );
};

FlowResultScreen.displayName = 'FlowResultScreen';

export { FlowResultScreen };
